import { Alert, Button, Dialog, DialogContent, DialogTitle, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { apiFetch } from '../api/client';
import { useBusiness } from '../contexts/BusinessContext';

type UploadRow = { upload_id: string; source_type: string; file_name?: string; status: string; created_at?: string };
type UploadDetail = UploadRow & { extraction_text?: string; draft_schema_json?: Record<string, unknown>; draft_ui_schema_json?: Record<string, unknown>; error_message?: string };
type TemplateRow = { template_id: string; name: string; description?: string };

export default function AdminDesignUploadsPage() {
  const { activeTenantId } = useBusiness();
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [sourceType, setSourceType] = useState('schema_json');
  const [configName, setConfigName] = useState('Generated Form Config');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [figmaUrl, setFigmaUrl] = useState('');
  const [figmaToken, setFigmaToken] = useState('');
  const [fileObj, setFileObj] = useState<File | null>(null);
  const [selected, setSelected] = useState<UploadDetail | null>(null);
  const [fullExtractionText, setFullExtractionText] = useState('');
  const [isLoadingExtraction, setIsLoadingExtraction] = useState(false);

  const load = async () => {
    if (!activeTenantId) return;
    setError('');
    const resp = await apiFetch(`/api/design-uploads?tenant_id=${encodeURIComponent(activeTenantId)}`);
    if (!resp.ok) {
      setError(await resp.text());
      return;
    }
    const data = (await resp.json()) as UploadRow[];
    setRows(Array.isArray(data) ? data : []);
  };
  const loadTemplates = async () => {
    const resp = await apiFetch('/api/form-templates');
    if (!resp.ok) return;
    const data = (await resp.json()) as TemplateRow[];
    const arr = Array.isArray(data) ? data : [];
    setTemplates(arr);
    if (!templateId && arr.length > 0) setTemplateId(arr[0].template_id);
  };
  useEffect(() => { void load(); }, [activeTenantId]);
  useEffect(() => { void loadTemplates(); }, []);

  const create = async () => {
    if (!activeTenantId || !fileObj) {
      setError('Select a file first');
      return;
    }
    setInfo('');
    const form = new FormData();
    form.append('tenant_id', activeTenantId);
    form.append('source_type', sourceType);
    form.append('file', fileObj);
    const resp = await apiFetch('/api/design-uploads/file', { method: 'POST', body: form });
    if (!resp.ok) { setError(await resp.text()); return; }
    setFileObj(null);
    setInfo('Upload created and processed.');
    try { localStorage.setItem('admin_phase1_upload_done', '1'); } catch {}
    await load();
  };

  const openDetail = async (uploadId: string) => {
    const resp = await apiFetch(`/api/design-uploads/${uploadId}`);
    if (!resp.ok) { setError(await resp.text()); return; }
    setSelected((await resp.json()) as UploadDetail);
  };

  const createConfigFromUpload = async () => {
    if (!selected) return;
    setInfo('');
    const resp = await apiFetch(`/api/design-uploads/${selected.upload_id}/create-config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: configName }),
    });
    if (!resp.ok) { setError(await resp.text()); return; }
    setInfo('Config created from draft schema.');
    await load();
    setSelected(null);
  };

  const regenerateWithLlm = async () => {
    if (!selected) return;
    setInfo('');
    const resp = await apiFetch(`/api/design-uploads/${selected.upload_id}/regenerate-draft-llm`, { method: 'POST' });
    if (!resp.ok) { setError(await resp.text()); return; }
    try {
      const body = await resp.json() as { warning?: string };
      if (body.warning) setInfo(body.warning);
      else setInfo('Draft regenerated successfully.');
    } catch {
      setInfo('Draft regenerated successfully.');
    }
    await openDetail(selected.upload_id);
  };

  const loadFullExtractionText = async () => {
    if (!selected) return;
    setIsLoadingExtraction(true);
    try {
      const resp = await apiFetch(`/api/design-uploads/${selected.upload_id}/extraction-text`);
      if (!resp.ok) {
        setError(await resp.text());
        return;
      }
      setFullExtractionText(await resp.text());
    } finally {
      setIsLoadingExtraction(false);
    }
  };

  const importFigma = async () => {
    if (!activeTenantId || !figmaUrl.trim()) {
      setError('Provide Figma URL');
      return;
    }
    const resp = await apiFetch('/api/design-uploads/figma-import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenant_id: activeTenantId, figma_url: figmaUrl.trim(), figma_token: figmaToken.trim() || undefined }),
    });
    if (!resp.ok) { setError(await resp.text()); return; }
    await load();
  };

  const createFromTemplate = async () => {
    if (!activeTenantId || !templateId) return;
    setInfo('');
    const tResp = await apiFetch(`/api/form-templates/${encodeURIComponent(templateId)}`);
    if (!tResp.ok) { setError(await tResp.text()); return; }
    const tpl = await tResp.json();
    const resp = await apiFetch('/api/design-uploads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tenant_id: activeTenantId,
        source_type: 'schema_json',
        file_name: `${templateId}.json`,
        payload_json: tpl,
      }),
    });
    if (!resp.ok) { setError(await resp.text()); return; }
    setInfo('Template upload created. Open it with View, then create config from draft.');
    try { localStorage.setItem('admin_phase1_upload_done', '1'); } catch {}
    await load();
  };

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e6ebf1' }}>
      <Typography variant="h6" fontWeight={700} sx={{ color: '#012676' }} gutterBottom>
        Design Uploads
      </Typography>
      {!activeTenantId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a tenant first in Tenants tab to use uploads and templates.
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField select size="small" label="Source" value={sourceType} onChange={(e) => setSourceType(e.target.value)} sx={{ minWidth: 180 }} disabled={!activeTenantId}>
          {['schema_json', 'pdf', 'image'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <Button variant="outlined" component="label" disabled={!activeTenantId}>
          Choose File
          <input
            hidden
            type="file"
            accept=".pdf,.docx,.json,.png,.jpg,.jpeg,.webp,.bmp,.tiff"
            onChange={(e) => setFileObj(e.target.files?.[0] || null)}
          />
        </Button>
        <Typography variant="body2" color="text.secondary">
          {fileObj ? fileObj.name : 'No file selected'}
        </Typography>
        <Button variant="contained" onClick={create} disabled={!activeTenantId || !fileObj}>Upload & Process</Button>
        <Button variant="outlined" onClick={() => void load()}>Refresh</Button>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Starter template"
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
          sx={{ minWidth: 300 }}
          disabled={!activeTenantId}
        >
          {templates.map((t) => <MenuItem key={t.template_id} value={t.template_id}>{t.name}</MenuItem>)}
        </TextField>
        <Button variant="outlined" onClick={createFromTemplate} disabled={!activeTenantId || !templateId}>
          Create Upload From Template
        </Button>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField size="small" label="Figma URL" value={figmaUrl} onChange={(e) => setFigmaUrl(e.target.value)} sx={{ minWidth: 360 }} disabled={!activeTenantId} />
        <TextField size="small" label="Figma Token (optional if backend env set)" value={figmaToken} onChange={(e) => setFigmaToken(e.target.value)} sx={{ minWidth: 260 }} disabled={!activeTenantId} />
        <Button variant="outlined" onClick={importFigma} disabled={!activeTenantId || !figmaUrl.trim()}>
          Import Figma
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {info && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      <TableContainer sx={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #e6ebf1', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead><TableRow><TableCell>Action</TableCell><TableCell>ID</TableCell><TableCell>Source</TableCell><TableCell>Name</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.upload_id}>
                <TableCell><Button size="small" variant="outlined" onClick={() => void openDetail(r.upload_id)}>View</Button></TableCell>
                <TableCell>{r.upload_id}</TableCell>
                <TableCell>{r.source_type}</TableCell>
                <TableCell>{r.file_name || '-'}</TableCell>
                <TableCell>{r.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(selected)} onClose={() => { setSelected(null); setFullExtractionText(''); }} maxWidth="lg" fullWidth>
        <DialogTitle>Upload Detail</DialogTitle>
        <DialogContent>
          {selected && (
            <Stack spacing={1}>
              <Typography variant="body2"><b>File:</b> {selected.file_name}</Typography>
              <Typography variant="body2"><b>Status:</b> {selected.status}</Typography>
              {selected.error_message && <Alert severity="error">{selected.error_message}</Alert>}
              <TextField
                size="small"
                label="New config name"
                value={configName}
                onChange={(e) => setConfigName(e.target.value)}
                sx={{ maxWidth: 360 }}
              />
              {selected.status !== 'processed' && (
                <Alert severity="info">
                  This upload must be in <b>processed</b> status before creating a config. Try regenerate first.
                </Alert>
              )}
              <Button variant="contained" onClick={createConfigFromUpload} disabled={selected.status !== 'processed'}>
                Create Config From Draft
              </Button>
              <Button variant="outlined" onClick={regenerateWithLlm}>
                Regenerate Draft With LLM
              </Button>
              <Button variant="outlined" onClick={loadFullExtractionText} disabled={isLoadingExtraction}>
                {isLoadingExtraction ? 'Loading full extracted text...' : 'Load Full Extracted Text'}
              </Button>
              <Typography variant="subtitle2">Extracted Text</Typography>
              <Paper variant="outlined" sx={{ p: 1, maxHeight: 180, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                {fullExtractionText || selected.extraction_text || '(no extracted text)'}
              </Paper>
              <Typography variant="subtitle2">Draft Schema</Typography>
              <Paper variant="outlined" sx={{ p: 1, maxHeight: 220, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(selected.draft_schema_json || {}, null, 2)}
              </Paper>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}

